import { useRef, useState } from 'react';
import { Thermometer, Lock, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import PortalPopover from './PortalPopover';
import { useToleranceStore } from '../../stores/toleranceStore';
import { useAuthStore, selectIsFree } from '../../stores/authStore';

// Thermal-Picker für den SubBar (Task #27 Phase E).
//
// Knopf mit Thermometer-Icon, der ein Popover mit den Wärme-Settings
// öffnet: Enable-Schalter, Tmin/Tmax-Inputs, kurze Erklärung. Pattern
// folgt dem MC-Samples-Picker — gleiches PortalPopover, gleiche
// Tastatur-Affordance, gleiche „pill"-Optik.
//
// Free-Verhalten: Knopf sichtbar mit Lock-Icon. Klick öffnet kein
// Popover, sondern leitet auf /settings/billing — gleiche
// Pro-Pull-Logik wie beim Monte-Carlo-Tab. Wenn eine Free-Sitzung ein
// Pro-Template mit thermal.enabled lädt, zeigt der Knopf den
// aktivierten Zustand an, ist aber nicht klick-/edit-bar.

const selectThermalState = (s) => ({
  thermal: s.thermal,
  setThermalEnabled: s.setThermalEnabled,
  setThermalRange: s.setThermalRange,
});

export default function ThermalPicker() {
  const { thermal, setThermalEnabled, setThermalRange } =
    useToleranceStore(useShallow(selectThermalState));
  const isFree = useAuthStore(selectIsFree);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const enabled = thermal?.enabled === true;
  const tMin = thermal?.tMin ?? -20;
  const tMax = thermal?.tMax ?? 80;

  const onClick = () => {
    if (isFree) {
      navigate('/settings/billing');
      return;
    }
    setOpen((o) => !o);
  };

  const buttonLabel = enabled ? `${tMin}…${tMax} °C` : 'Wärme';

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        title={isFree
          ? 'Wärmeausdehnung berücksichtigen - Pro-Feature. Auf Pro upgraden, um Material + Temperaturbereich zu setzen.'
          : (enabled
              ? `Wärmeausdehnung aktiv: ${tMin} bis ${tMax} °C (Bezug 20 °C). Klick zum Anpassen.`
              : 'Wärmeausdehnung berücksichtigen: Material pro Maß + Tmin/Tmax. Verschiebt das Schließmaß gegenüber dem Zeichnungsmaß bei 20 °C.')}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold border transition-colors ${
          isFree
            ? 'bg-surface-2 text-ink-4 border-line hover:text-warn'
            : enabled
              ? 'bg-brand-soft text-brand border-brand/40'
              : 'bg-surface-2 text-ink-3 border-line hover:text-ink-2'
        }`}
        // Padding etwas knapper im Icon-only-State (unter xl), damit
        // der Button nicht hohl wirkt; bei voller Breite (xl+)
        // bekommt das Label genug Luft.
        style={{ height: 30, padding: '0 10px', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
      >
        <Thermometer size={12} aria-hidden="true" />
        {/* Label verschwindet unter xl (1280 px) — im SubBar wird's
            sonst eng. Active-State bleibt am Brand-Background +
            Tooltip sichtbar. */}
        <span className="hidden 2xl:inline">{buttonLabel}</span>
        {isFree && <Lock size={10} aria-hidden="true" />}
      </button>

      <PortalPopover anchorRef={ref} open={open && !isFree} onClose={() => setOpen(false)} minWidth={280}>
        {/* Fixe Breite, damit number-Inputs (die per Browser-Default
            ~150 px breit sind und ohne min-width:0 nicht flex-schrumpfen)
            den Popover nicht über die Wunschbreite hinausziehen. Ohne
            das wurde der Popover real ~430 px breit, was nach dem
            Anchor-Right-Flip optisch wuchtig wirkte. */}
        <div className="bg-surface border border-line" style={{ width: 280, borderRadius: 'var(--radius-md)', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Header mit Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Thermometer size={16} className="text-brand" aria-hidden="true" />
            <span className="text-ink-2" style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
              Wärmeausdehnung
            </span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setThermalEnabled(e.target.checked)}
                style={{ width: 14, height: 14, cursor: 'pointer' }}
              />
              <span className="text-ink-2" style={{ fontSize: 11.5, fontWeight: 600 }}>
                aktiv
              </span>
            </label>
          </div>

          {/* Beschreibung — kurz, mit Bezug zur Norm */}
          <div className="text-ink-3" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
            Bauteilmaße sind nach <span className="num">ISO 1</span> bei <span className="num">20 °C</span> definiert.
            Bei Betrieb außerhalb verschiebt sich das Schließmaß je nach Material und Temperaturbereich.
            Aktiviere die Spalte „Material" in der Tabelle und gib den Betriebsbereich an.
          </div>

          {/* Temperatur-Inputs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, flex: 1 }}>
              <span className="text-ink-3" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Tmin (°C)
              </span>
              <input
                type="number"
                value={tMin}
                disabled={!enabled}
                onChange={(e) => setThermalRange(Number(e.target.value), tMax)}
                step="1"
                className="num bg-surface border border-line text-ink-2"
                style={{ width: '100%', minWidth: 0, height: 28, padding: '0 8px', fontSize: 12, borderRadius: 'var(--radius-sm)', outline: 'none', opacity: enabled ? 1 : 0.5 }}
              />
            </label>
            <span className="text-ink-4" style={{ alignSelf: 'flex-end', paddingBottom: 6, fontSize: 12 }}>…</span>
            <label style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, flex: 1 }}>
              <span className="text-ink-3" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Tmax (°C)
              </span>
              <input
                type="number"
                value={tMax}
                disabled={!enabled}
                onChange={(e) => setThermalRange(tMin, Number(e.target.value))}
                step="1"
                className="num bg-surface border border-line text-ink-2"
                style={{ width: '100%', minWidth: 0, height: 28, padding: '0 8px', fontSize: 12, borderRadius: 'var(--radius-sm)', outline: 'none', opacity: enabled ? 1 : 0.5 }}
              />
            </label>
          </div>

          {/* Hinweis nur bei wirklich extremen Bereichen: <-50 °C
              (Kryo) oder >+200 °C (Hochtemperatur). In diesem Fenster
              ist die Konstant-α-Annahme nicht mehr tragfähig; bis
              dahin sind die Werte für Konstruktionsentscheidungen
              hinreichend genau und ein Hinweis wäre Lärm. */}
          {enabled && (tMin < -50 || tMax > 200) && (
            <div
              className="text-warn bg-warn/10 border border-warn/30"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: 8, borderRadius: 6, fontSize: 11, lineHeight: 1.4 }}
            >
              <Info size={12} className="shrink-0" style={{ marginTop: 2 }} aria-hidden="true" />
              <span>
                Großer Temperaturbereich: bei Kryo (&lt;−50 °C) oder Hochtemperatur (&gt;+200 °C) ist
                der Ausdehnungskoeffizient nicht mehr konstant - die Werte hier sind grobe Richtwerte.
              </span>
            </div>
          )}
        </div>
      </PortalPopover>
    </>
  );
}
